import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { showAlert } from '../../../services/alert';
import { useGetAllAgriInputProductsQuery } from '../../../services/mVPGraphQlService';

export type ProductOption = { value: string; label: string };


export const formatProductLabel = (product?: {
  productName?: string;
  advantaMaterialCode?: string;
}) =>
  [product?.productName, product?.advantaMaterialCode]
    .filter(Boolean)
    .join(' · ');


type ProductFilters = {
  region?: string | null;
  crop?: string | null;
  hybrid?: string | null;
};

export const useProductOptions = (
  open: boolean,
  filters: ProductFilters
) => {
  const dispatch = useDispatch();
  const wsId = useSelector(
    (state: RootState) => state.country.wsId
  );

  const region = filters.region?.trim() || '';
  const cropName = filters.crop?.trim() || '';
  const hybridProductionCode = filters.hybrid?.trim() || '';
  const enabled =
    open &&
    !!region &&
    !!cropName &&
    !!hybridProductionCode;

  const { currentData, isFetching, isError } =
    useGetAllAgriInputProductsQuery(
      { wsId, region, cropName, hybridProductionCode },
      { skip: !enabled }
    );
  const result = enabled
    ? currentData?.data?.GetAllAgriInputProducts
    : undefined;

  const productOptions = useMemo(
    () =>
      (result?.products ?? [])
        .filter((p) => p.productId != null && p.active !== false)
        .map(
          (p): ProductOption => ({
            value: String(p.productId),
            label: formatProductLabel(p) || String(p.productId),
          })
        ),
    [result]
  );

  const uplByProductId = useMemo(() => {
    const map: Record<string, boolean> = {};
    (result?.products ?? []).forEach((p) => {
      if (p.productId != null) {
        map[String(p.productId)] = p.uplProduct === true;
      }
    });
    return map;
  }, [result]);

  useEffect(() => {
    if (!enabled) return;
    const status = result?.responseStatus;
    if (isError || (status && status.status !== 'SUCCESS')) {
      dispatch(
        showAlert({
          content:
            status?.errorMessages?.[0] ||
            'Unable to load products',
          severity: 'error',
        })
      );
    }
  }, [enabled, isError, result, dispatch]);

  const loaded =
    enabled &&
    !isFetching &&
    result?.responseStatus?.status === 'SUCCESS';

  return {
    productOptions,
    uplByProductId,
    loading: isFetching,
    loaded,
  };
};
